import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { setUser, setAuthError, setAuthLoading } from "../../store/actions";
import { signInWithGoogle, logOut } from "../../firebase";
import Modal from "react-modal";
import "./AuthModal.css";

const AuthModal = ({
    isOpen,
    onClose,
    user,
    authError,
    authLoading,
    setUserAction,
    setAuthErrorAction,
    setAuthLoadingAction
}) => {
    const [imageError, setImageError] = useState(false);
    const [modalMode, setModalMode] = useState("login"); // "login" or "signup"
    const [photoURL, setPhotoURL] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        if (user?.photoURL) {
            // Use the photoURL directly without cache-busting to avoid rate limiting
            // Google profile images are already cached by the browser
            setPhotoURL(user.photoURL);
        } else {
            setPhotoURL(null);
        }
    }, [user?.photoURL]);

    const handleImageError = (e) => {
        // If image fails to load, try once more without cache-busting
        if (!imageError) {
            setImageError(true);
            if (imgRef.current) {
                imgRef.current.src = user.photoURL;
            }
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setAuthLoadingAction(true);
            setAuthErrorAction(null);
            const userData = await signInWithGoogle();
            setUserAction(userData);
            onClose();
        } catch (error) {
            console.error("Google sign in error:", error);
            setAuthErrorAction(error.message);
        }
    };

    const handleSignOut = async () => {
        try {
            setAuthLoadingAction(true);
            await logOut();
            setUserAction(null);
        } catch (error) {
            console.error("Sign out error:", error);
            setAuthErrorAction(error.message);
        }
    };

    // If user is already logged in, show profile
    if (user) {
        return (
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                className="auth_modal"
                overlayClassName="auth_modal_overlay"
                appElement={document.getElementById('root')}
            >
                <div className="auth_modal_content">
                    <button className="auth_modal_close" onClick={onClose}>×</button>

                    <div className="user_profile">
                        {photoURL ? (
                            <img
                                ref={imgRef}
                                src={photoURL}
                                alt="Profile"
                                className="user_avatar"
                                onError={handleImageError}
                            />
                        ) : (
                            <div className="user_avatar_placeholder">
                                {user.displayName ? user.displayName.charAt(0).toUpperCase() : "U"}
                            </div>
                        )}
                        <h2 className="user_name">{user.displayName || "User"}</h2>
                        <p className="user_email">{user.email}</p>
                    </div>

                    <button
                        className="auth_button sign_out_button"
                        onClick={handleSignOut}
                        disabled={authLoading}
                    >
                        {authLoading ? "Signing out..." : "Sign Out"}
                    </button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="auth_modal"
            overlayClassName="auth_modal_overlay"
            appElement={document.getElementById('root')}
        >
            <div className="auth_modal_content">
                <button className="auth_modal_close" onClick={onClose}>×</button>

                <div className="auth_modal_header">
                    <h2>{modalMode === "login" ? "Welcome Back" : "Create Account"}</h2>
                    <p>Sign in to host or join meetings</p>
                </div>

                {authError && (
                    <div className="auth_error">
                        {authError}
                    </div>
                )}

                <div className="auth_buttons_container">
                    <button
                        className="auth_button google_button"
                        onClick={handleGoogleSignIn}
                        disabled={authLoading}
                    >
                        <svg className="google_icon" viewBox="0 0 24 24" width="24" height="24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        {authLoading ? "Signing in..." : "Continue with Google"}
                    </button>
                </div>

                <div className="auth_modal_footer">
                    <p>
                        {modalMode === "login" ? "Don't have an account? " : "Already have an account? "}
                        <span
                            className="auth_mode_switch"
                            onClick={() => setModalMode(modalMode === "login" ? "signup" : "login")}
                        >
                            {modalMode === "login" ? "Sign up" : "Sign in"}
                        </span>
                    </p>
                </div>

                <div className="auth_terms">
                    <p>
                        By continuing, you agree to our Terms of Service and Privacy Policy.
                    </p>
                </div>
            </div>
        </Modal>
    );
};

const mapActionsToProps = (dispatch) => {
    return {
        setUserAction: (user) => dispatch(setUser(user)),
        setAuthErrorAction: (error) => dispatch(setAuthError(error)),
        setAuthLoadingAction: (loading) => dispatch(setAuthLoading(loading)),
    };
};

const mapStateToProps = (state) => {
    return {
        user: state.user,
        authError: state.authError,
        authLoading: state.authLoading,
    };
};

export default connect(mapStateToProps, mapActionsToProps)(AuthModal);
